import { RouteName } from '@/router/constants/routeNames';
import { UserClaim } from './userClaim';
import { SortDirection } from './sortDirection';
import { ReportColumn } from '@/models/report';
import { AccountingTypeLabel } from './accountingType';

export type SidebarItem = ParentSidebarItem | LinkSidebarItem;

export type BaseSidebarItem = {
  label: string;
  icon: string;
  active?: boolean;
  dataCy: string;
  claims?: UserClaim[];
};
export type ParentSidebarItem = BaseSidebarItem & {
  type: 'parent';
  children: SidebarItem[];
  expanded?: boolean;
};
export type LinkSidebarItem = BaseSidebarItem & {
  type: 'link';
  route: string;
  params?: Record<string, string>;
  query?: Record<string, string | number>;
};

export const SidebarItems: SidebarItem[] = [
  {
    type: 'link',
    label: 'sidebar.dashboard',
    route: RouteName.Dashboard,
    icon: 'mdi-view-dashboard',
    dataCy: 'side-bar-dashboard'
  },
  {
    type: 'link',
    label: 'sidebar.createReport',
    route: RouteName.CreateReport,
    icon: 'mdi-pencil',
    dataCy: 'create-report',
    params: { reportType: 'header' },
    claims: [UserClaim.Claim_Admin, UserClaim.Claim_Report_Create]
  },
  {
    type: 'parent',
    label: 'sidebar.financialReports',
    icon: 'mdi-note-text',
    dataCy: 'financial-reports',
    claims: [
      UserClaim.Claim_Admin,
      UserClaim.Claim_Report_Review,
      UserClaim.Claim_Report_Review_All,
      UserClaim.Claim_Report_Economy_Review_All
    ],
    children: [
      {
        type: 'link',
        label: 'sidebar.activeReports',
        route: RouteName.ActiveReports,
        icon: 'mdi-clipboard-text',
        dataCy: 'active-reports',
        params: { reportsType: 'own-reports' },
        query: { page: 1, perPage: 10, sortBy: ReportColumn.Status, sortDesc: SortDirection.Desc }
      },
      {
        type: 'link',
        label: 'sidebar.consolidatedReports',
        route: RouteName.ConsolidatedReports,
        icon: 'fa:file-export',
        dataCy: 'consolidated-reports',
        query: { page: 1, perPage: 10 },
        claims: [
          UserClaim.Claim_Admin,
          UserClaim.Claim_Report_Economy_Review_All
        ]
      },
      {
        type: 'link',
        label: 'sidebar.historicalReports',
        route: RouteName.HistoricReports,
        icon: 'mdi-clipboard-text-history',
        dataCy: 'historic-reports',
        params: { reportsType: 'own-reports' },
        query: { page: 1, perPage: 10 }
      }
    ]
  },
  {
    type: 'parent',
    label: 'sidebar.childOrganizations',
    icon: 'mdi-domain',
    dataCy: 'subordinate-kjs',
    claims: [
      UserClaim.Claim_Admin,
      UserClaim.Claim_Report_Review,
      UserClaim.Claim_Report_Review_All,
      UserClaim.Claim_Report_Economy_Review_All
    ],
    children: [
      {
        type: 'link',
        label: 'childOrganizations.organizations',
        route: RouteName.ChildOrganizationsList,
        icon: 'mdi-bank',
        dataCy: 'organizations-kjs',
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'childOrganizations.activeReports',
        route: RouteName.ChildOrganizationsActiveReports,
        icon: 'mdi-clipboard-text',
        dataCy: 'active-reports-kjs',
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'childOrganizations.historicReports',
        route: RouteName.ChildOrganizationsHistoricReports,
        icon: 'mdi-clipboard-text-history',
        dataCy: 'historic-reports-kjs',
        query: { page: 1, perPage: 10 }
      }
    ]
  },
  {
    type: 'parent',
    label: 'sidebar.userAdministration',
    icon: 'mdi-administrator',
    dataCy: 'side-bar-administration',
    claims: [
      UserClaim.Claim_Admin,
      UserClaim.Claim_User_Create,
      UserClaim.Claim_User_Review,
      UserClaim.Claim_Local_User_Create,
      UserClaim.Claim_Local_User_Review,
      UserClaim.Claim_Report_Type_Review,
      UserClaim.Claim_Report_Type_Period_Lock,
      UserClaim.Claim_Report_Review_All,
      UserClaim.Claim_Report_Economy_Review_All
    ],
    children: [
      {
        type: 'link',
        label: 'registry.reportTypes',
        route: RouteName.ReportTypes,
        icon: 'mdi-chart-box',
        dataCy: 'administration-report',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Report_Type_Review],
        params: { accountingType: AccountingTypeLabel.BUDGET }
      },
      {
        type: 'link',
        label: 'sidebar.administrativeStatistics',
        route: RouteName.ReportTypePeriodStatistics,
        query: { page: 1, perPage: 10, 'filter[status]': '2' },
        dataCy: 'report-statiscics',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Report_Review_All, UserClaim.Claim_Report_Economy_Review_All],
        icon: 'mdi-chart-pie'
      },
      {
        type: 'link',
        label: 'userAdministration.administration',
        route: RouteName.UserManagement,
        icon: 'mdi-account-supervisor',
        dataCy: 'user-management',
        claims: [
          UserClaim.Claim_Admin,
          UserClaim.Claim_User_Create,
          UserClaim.Claim_User_Review,
          UserClaim.Claim_Local_User_Create,
          UserClaim.Claim_Local_User_Review
        ],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'userAdministration.userNotification',
        route: RouteName.UserNotifications,
        icon: 'mdi-bell',
        dataCy: 'user-notification',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Notification_Create],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'userAdministration.explicitHierarchy',
        route: RouteName.ExplicitHierarchies,
        icon: 'mdi-format-line-weight',
        dataCy: 'explicit-hierarchy',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Explicit_Hierarchy_Review],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'sidebar.userRegistration',
        route: RouteName.UserRegistrations,
        icon: 'mdi-domain',
        dataCy: 'user-registration',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_User_Create],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'userAdministration.registrationChange',
        route: RouteName.UserRegistrationChange,
        icon: 'mdi-pencil',
        dataCy: 'registration-change',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Registration_Edit]
      }
    ]
  },
  {
    type: 'parent',
    label: 'sidebar.registries',
    icon: 'mdi-archive',
    dataCy: 'data-tables',
    claims: [
      UserClaim.Claim_Admin,
      UserClaim.Claim_Registry_Review,
      UserClaim.Claim_Automatic_Processing_Code_Lock,
      UserClaim.Claim_Automatic_Processing_Code_Review
    ],
    children: [
      {
        type: 'link',
        label: 'registry.automaticProcessingCodes',
        route: RouteName.AutomaticProcessingCodes,
        icon: ' mdi-format-list-bulleted',
        dataCy: 'automatic-processing-codes',
        query: { page: 1, perPage: 10, 'filter[accountingType]': 1 }
      },
      {
        type: 'link',
        label: 'registry.reportTypeSchedules',
        route: RouteName.ReportTypeSchedules,
        icon: ' mdi-format-list-bulleted',
        dataCy: 'report-type-schedules',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Report_Type_Review],
        params: { accountingType: AccountingTypeLabel.BUDGET }
      },
      {
        type: 'link',
        label: 'registry.budgetUsers',
        route: RouteName.BudgetUsers,
        icon: ' mdi-format-list-bulleted',
        dataCy: 'budget-users',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Registry_Review],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'registry.municipalities',
        route: RouteName.Municipalities,
        icon: ' mdi-format-list-bulleted',
        dataCy: 'municipalities',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Registry_Review],
        query: { page: 1, perPage: 10 }
      },
      {
        type: 'link',
        label: 'registry.treasuryBranches',
        route: RouteName.TreasuryBranches,
        icon: ' mdi-format-list-bulleted',
        dataCy: 'treasury-branches',
        claims: [UserClaim.Claim_Admin, UserClaim.Claim_Registry_Review],
        query: { page: 1, perPage: 10 }
      }
    ]
  }
] as const;
